import React from "react";
import PropTypes from "prop-types";
import { injectIntl, intlShape } from "react-intl";
import { Button } from "@yoast/ui-library";

import ErrorDisplay from "../../../errors/ErrorDisplay";
import useRequest from "../../../reactHooks/useRequest";

import { messages } from "./messages";

/**
 * Picks the most specific error message available, falling back to the generic one.
 *
 * @param {Object|null} envelopeError The error from a 200 + {success:false} envelope.
 * @param {Object|null} requestError  The error thrown by useRequest.
 * @param {Object}      intl          react-intl's intl object.
 *
 * @returns {string} The user-facing error message.
 */
const resolveUserMessage = ( envelopeError, requestError, intl ) => {
	return envelopeError?.user_message ||
		requestError?.error?.user_message ||
		intl.formatMessage( messages.genericError );
};

/**
 * On-demand payment method display for a subscription.
 * Fetches payment method details (brand, last 4 digits) from the API when clicked.
 *
 * @param {Object} props The component props.
 *
 * @returns {JSX.Element} The ShowPaymentMethod component.
 */
const ShowPaymentMethod = ( { subscriptionId, intl } ) => {
	const { data, isLoading, error, reload } = useRequest(
		`Subscriptions/${ subscriptionId }/payment-method`,
		"GET",
		{},
		{},
		false,
	);

	// Upstream failures come back as 200 + {success:false}; useRequest treats that as "data".
	const envelopeError = data?.success === false ? data.error : null;
	const paymentMethod = data?.payment_method ?? null;

	if ( paymentMethod ) {
		// Prefer i18n copy for the "unrecorded" sentinel (subscription has no payment method
		// on file — typically a support-created order). Otherwise render the backend label.
		const label = paymentMethod.type === "unrecorded"
			? intl.formatMessage( messages.noPaymentMethod )
			: paymentMethod.display_name;
		return <span>{ label }</span>;
	}

	if ( error || envelopeError ) {
		const userMessage = resolveUserMessage( envelopeError, error, intl );

		return (
			<>
				<ErrorDisplay error={ { message: userMessage, type: "error" } } />
				<Button
					variant="secondary"
					size="small"
					disabled={ isLoading }
					isLoading={ isLoading }
					onClick={ reload }
				>
					{ intl.formatMessage( messages.retry ) }
				</Button>
			</>
		);
	}

	return (
		<Button
			variant="secondary"
			size="small"
			disabled={ isLoading }
			isLoading={ isLoading }
			onClick={ reload }
		>
			{ isLoading
				? intl.formatMessage( messages.fetching )
				: intl.formatMessage( messages.showPaymentMethod )
			}
		</Button>
	);
};

ShowPaymentMethod.propTypes = {
	subscriptionId: PropTypes.string.isRequired,
	intl: intlShape.isRequired,
};

export default injectIntl( ShowPaymentMethod );
export { ShowPaymentMethod as RawShowPaymentMethod };
